import api from '../client'

export const postApi = {
  create: async (orgSlug, post) => {
    const response = await api.post(`/${orgSlug}/posts`, post)
    return response.data
  },
  
  list: async (orgSlug, params = {}) => {
    const response = await api.get(`/${orgSlug}/posts`, { params })
    return response.data
  },
  
  get: async (orgSlug, postId) => {
    const response = await api.get(`/${orgSlug}/posts/${postId}`)
    return response.data
  },
  
  update: async (orgSlug, postId, updates) => {
    const response = await api.patch(`/${orgSlug}/posts/${postId}`, updates)
    return response.data
  },
  
  delete: async (orgSlug, postId) => {
    await api.delete(`/${orgSlug}/posts/${postId}`)
  },
  archive: async (orgSlug, postId, reason = '') => {
    const response = await api.post(`/${orgSlug}/posts/${postId}/archive`, { reason })
    return response.data
  },

  restore: async (orgSlug, postId) => {
    const response = await api.delete(`/${orgSlug}/posts/${postId}/archive`)
    return response.data
  },


  undoPendingDelete: async (orgSlug, postId) => {
    const response = await api.delete(`/${orgSlug}/posts/${postId}/pending-delete`)
    return response.data
  },

  forceDelete: async (orgSlug, postId) => {
    const response = await api.post(`/${orgSlug}/posts/${postId}/force-delete`)
    return response.data
  },

  forceDeleteMany: async (orgSlug, postIds) => {
    const response = await api.post(`/${orgSlug}/posts/batch-force-delete`, { post_ids: postIds })
    return response.data
  },

  deleteMany: async (orgSlug, postIds) => {
    const response = await api.post(`/${orgSlug}/posts/batch-delete`, { post_ids: postIds })
    return response.data
  },

  // Archives (archived: true) or restores (archived: false) up to 500 posts
  // in one all-or-nothing request.
  archiveMany: async (orgSlug, { postIds, archived, reason = '' }) => {
    const response = await api.post(`/${orgSlug}/posts/batch-archive`, { post_ids: postIds, archived, reason })
    return response.data
  },
  
  approve: async (orgSlug, postId) => {
    const response = await api.post(`/${orgSlug}/posts/${postId}/approve`)
    return response.data
  },
  
  reject: async (orgSlug, postId, reason) => {
    const response = await api.post(`/${orgSlug}/posts/${postId}/reject`, { reason })
    return response.data
  },
  
  generateUploadUrl: async (orgSlug, contentType) => {
    const response = await api.post(`/${orgSlug}/upload-url`, { content_type: contentType })
    return response.data
  },

  skipApproval: async (orgSlug, postId) => {
    const response = await api.post(`/${orgSlug}/posts/${postId}/skip-approval`)
    return response.data
  },

  denormalize: async (orgSlug, postId) => {
    const response = await api.post(`/${orgSlug}/posts/${postId}/denormalize`)
    return response.data
  },

  getDraft: async (orgSlug) => {
    const response = await api.get(`/${orgSlug}/posts/draft`)
    return response.data
  },

  saveDraft: async (orgSlug, draft) => {
    const response = await api.patch(`/${orgSlug}/posts/draft`, draft)
    return response.data
  },

  deleteDraft: async (orgSlug) => {
    const response = await api.delete(`/${orgSlug}/posts/draft`)
    return response.data
  }
}

export const surfacesApi = {
  list: async (orgSlug) => {
    const response = await api.get(`/${orgSlug}/surfaces`)
    return response.data
  },
  get: async (orgSlug, surfaceId) => {
    const response = await api.get(`/${orgSlug}/surfaces/${surfaceId}`)
    return response.data
  },
  create: async (orgSlug, payload) => {
    const response = await api.post(`/${orgSlug}/surfaces`, payload)
    return response.data
  },
  update: async (orgSlug, surfaceId, updates) => {
    const response = await api.patch(`/${orgSlug}/surfaces/${surfaceId}`, updates)
    return response.data
  },
  delete: async (orgSlug, surfaceId) => {
    const response = await api.delete(`/${orgSlug}/surfaces/${surfaceId}`)
    return response.data
  },
}

export const creatorApi = {
  search: async (orgSlug, query, { limit, offset, scope, organizationIds } = {}) => {
    const response = await api.get(`/${orgSlug}/creators`, {
      params: {
        q: query,
        ...(limit ? { limit } : {}),
        ...(offset ? { offset } : {}),
        ...(scope ? { scope } : {}),
        ...(organizationIds?.length ? { organization_ids: organizationIds.join(',') } : {}),
      },
    })
    return response.data
  },
  add: async (orgSlug, displayName) => {
    const response = await api.post(`/${orgSlug}/creators`, { display_name: displayName })
    return response.data
  },
  remove: async (orgSlug, userId) => {
    await api.delete(`/${orgSlug}/creators/${userId}`)
  },
  update: async (orgSlug, creatorId, updates) => {
    const response = await api.put(`/${orgSlug}/creators/${creatorId}`, updates)
    return response.data
  },
  uploadAvatar: async (orgSlug, creatorId, file) => {
    const form = new FormData()
    form.append('avatar', file)
    const response = await api.post(`/${orgSlug}/creators/${creatorId}/avatar`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },
}

export const tagApi = {
  list: async (orgSlug, activeOnly = false) => {
    const response = await api.get(`/${orgSlug}/tags`, { params: { active_only: activeOnly } })
    return response.data
  },
  create: async (orgSlug, tag) => {
    const response = await api.post(`/${orgSlug}/tags`, tag)
    return response.data
  },
  get: async (orgSlug, tagId) => {
    const response = await api.get(`/${orgSlug}/tags/${tagId}`)
    return response.data
  },
  update: async (orgSlug, tagId, updates) => {
    const response = await api.put(`/${orgSlug}/tags/${tagId}`, updates)
    return response.data
  },
  delete: async (orgSlug, tagId) => {
    await api.delete(`/${orgSlug}/tags/${tagId}`)
  }
}

export const kambiApi = {
  getOfferings: async (orgSlug) => {
    const response = await api.get(`/${orgSlug}/kambi/offerings`)
    return response.data
  },
  updateOfferings: async (orgSlug, offeringIds) => {
    const response = await api.patch(`/${orgSlug}/kambi/offerings`, { offering_ids: offeringIds })
    return response.data
  },
  validateOutcomes: async (orgSlug, outcomeIds, offeringIds) => {
    const response = await api.post(`/${orgSlug}/kambi/validate-outcomes`, {
      outcome_ids: outcomeIds,
      offering_ids: offeringIds
    })
    return response.data
  }
}

export const approvalApi = {
  getPending: async (orgSlug) => {
    const response = await api.get(`/${orgSlug}/approvals/pending`)
    return response.data
  },
  approve: async (orgSlug, id, data = {}) => {
    const response = await api.post(`/${orgSlug}/approvals/${id}/approve`, data)
    return response.data
  },
  reject: async (orgSlug, id, data) => {
    const response = await api.post(`/${orgSlug}/approvals/${id}/reject`, data)
    return response.data
  }
}
